import { BoxGeometry, Group, Material, Mesh, Object3D, Vector3 } from 'three';
import { ArticlesCreator } from '@project/components/article/presenter/article-creator';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleVO } from '@article/model/vo/article.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { CustomArticleUtils } from '@article/util/custom-article-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';
import { ConnectionAreaVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { CommonConstants } from '@3d-scene/constants/common-constants';
import { FaceUtils } from '@3d-scene/common/util/face-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { UserModel } from '@user/model/user.model';
import { ProjectUserModel } from '@project/model/project-user.model';
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';

export class CustomArticlePostParseProcessor {

  private static readonly TEXTURE_MESH_RENDER_ORDER: number = 1;

  public static process(articleContainer: ArticleContainer, userModel: UserModel, projectUserModel: ProjectUserModel): void {
    if (articleContainer.articleObject3DDataShared) {
      throw new Error('Modifying a shared article geometry is not allowed!');
    }
    articleContainer.connections.forEach((connection: Connection) => connection.cloneMesh3DResources());
    const article: ArticleVO = articleContainer.article;
    const articleUsage = articleContainer.articleUsage;
    let userId: number = CustomArticleUtils.getCustomImageUserId(articleUsage);
    if (NumberUtils.isNotNumber(userId) && userModel.isUserLoggedIn) {
      userId = projectUserModel.getProjectOwnerId();
      ArticleUsageUtils.setArticleUsageInfoPropertyIntValue(articleUsage, ArticleUsageInfoPropertyDef.CUSTOM_IMAGE_USER_ID, userId);
    }
    const articleImageHash: string = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(articleUsage,
      ArticleUsageInfoPropertyDef.CUSTOM_ARTICLE_IMAGE_HASH);
    if (articleImageHash) {
      CustomArticleUtils.applyImageHashToArticleConnection(articleUsage, article, articleImageHash);
      PropertyUtils.removePropertiesByNames(articleUsage.infoProperties, [ArticleUsageInfoPropertyDef.CUSTOM_ARTICLE_IMAGE_HASH]);
    }
    const articleWidth: number = CustomArticleUtils.getCustomArticleWidth(articleUsage);
    const articleHeight: number = CustomArticleUtils.getCustomArticleHeight(articleUsage);
    const articleDepth: number = CustomArticleUtils.getCustomArticleDepth(articleUsage);
    let articleDepthOffset: number = CustomArticleUtils.getCustomArticleDepthOffset(articleUsage);
    if (!NumberUtils.isNumber(articleDepthOffset)) {
      articleDepthOffset = 0;
    }
    this.updateCustomArticleGeometry(articleContainer, article, new Vector3(articleWidth, articleHeight, articleDepth), articleDepthOffset);
  }

  private static updateCustomArticleGeometry(container: ArticleContainer, articleVO: ArticleVO, articleSize: Vector3,
    articleDepthOffset: number): void {
    const connectionNames: string[] = new Array<string>();
    for (const metaElement of articleVO.metaElements) {
      connectionNames.push(metaElement.name);
    }

    const material: Material = Object3DUtils.findFirstMaterial(container, connectionNames);
    material.opacity = 1;
    this.removeArticleMeshes(container, connectionNames);

    // resize giving connection
    const givingArea: ConnectionAreaVO = ArticleUtils.findFirstConnectionArea(articleVO.metaElements, ConnectionAreaVO.SIDE_GIVING);
    if (givingArea) {
      const givingConnection: Connection = container.findConnectionByName(givingArea.name);
      this.resizeGivingConnection(givingConnection, articleSize);
    }

    // resize image connection
    const imageArea: TextureAreaVO = CustomArticleUtils.getCustomImageArea(articleVO);
    if (imageArea) {
      const imageConnection: Connection = container.findConnectionByName(imageArea.name);
      this.resizeImageConnection(imageConnection, articleSize, articleDepthOffset);
      imageConnection.bitmapRotation = 0;
      imageConnection.refreshTexture();
      imageConnection.isTextureLoaded = false;
    }

    // resize article geometry
    const customArticleGeometry: BoxGeometry = new BoxGeometry(articleSize.x, articleSize.y, articleSize.z);
    const customArticleMesh: Mesh = new Mesh(customArticleGeometry, material);
    const articleOffset: Vector3 = new Vector3(-(articleSize.x / 2), articleSize.y / 2 + CommonConstants.EPSILON, articleDepthOffset);
    FaceUtils.moveMeshByOffset(customArticleMesh, articleOffset);
    container.addChildToModel(customArticleMesh);
  }

  private static removeArticleMeshes(baseContainer: Object3D, connectionNames: string[]): void {
    const children: Object3D[] = Object3DUtils.getChildrenRecursively(baseContainer);
    for (const child of children) {
      this.removeArticleMeshes(child, connectionNames);

      if (IsThreeJsType.mesh(child)) {
        if (connectionNames.indexOf(child.name) === -1) {
          baseContainer.remove(child);
        }
      }
    }
  }

  private static resizeGivingConnection(givingConnection: Connection, articleSize: Vector3): void {
    const oldConnectionMesh = givingConnection.mesh;
    const parentContainer: Group = oldConnectionMesh.parent as Group;
    const connectionMesh: Mesh = ArticlesCreator.stretchConnection(givingConnection, articleSize.x, true);
    if (connectionMesh) {
      parentContainer.remove(oldConnectionMesh);
      parentContainer.add(connectionMesh);
      const connectionMiddlePoint: Vector3 = MathUtils.calculateMiddlePointByRectangle(givingConnection.localRectangle);
      const targetMiddlePoint: Vector3 = new Vector3(-articleSize.x / 2, 0, 0);
      FaceUtils.moveMeshByOffset(connectionMesh as Mesh, targetMiddlePoint.sub(connectionMiddlePoint));
      (connectionMesh.material as Material).visible = false;
    }
  }

  private static resizeImageConnection(imageConnection: Connection, articleSize: Vector3, articleDepthOffset: number): void {
    const connectionMesh = imageConnection.mesh;
    if (IsThreeJsType.mesh(connectionMesh)) {
      const connectionMiddlePoint: Vector3 = MathUtils.calculateMiddlePointByRectangle(imageConnection.localRectangle);
      const targetMiddlePoint: Vector3 = new Vector3(-articleSize.x / 2, articleSize.y + CommonConstants.EPSILON * 10, articleDepthOffset);
      const offset: Vector3 = targetMiddlePoint.sub(connectionMiddlePoint);
      FaceUtils.moveMeshByOffset(connectionMesh, offset);
      connectionMesh.renderOrder = CustomArticlePostParseProcessor.TEXTURE_MESH_RENDER_ORDER;
    }
  }
}
